/**
 * <flagship-store-careers>
 *
 * Fetches the Smoothie HR widget rendered on /pages/career-opportunities,
 * filters the job-listing cards by the `tag` attribute matching this card's
 * `data-store-location`, and renders matching listings inline. Falls back
 * to an empty-state message + description when zero jobs match.
 *
 * Required data attributes (set in blocks/flagship-store-careers.liquid):
 *   data-store-location     — store title (e.g. "Costa Mesa"); used as the
 *                             filter against each job card's `tag` attr.
 *   data-empty-message      — title text shown in the empty state.
 *   data-empty-description  — HTML description shown under the empty title.
 *
 * Required inner markup:
 *   <div data-careers-list>…skeleton placeholders…</div>
 *
 * The fetch URL is intentionally RELATIVE so the same code works on dev,
 * staging, preview, and production stores (each uses its own /pages/...).
 */
if (!customElements.get('flagship-store-careers')) {
  class FlagshipStoreCareers extends HTMLElement {
    constructor() {
      super();
      this.storeLocation = this.dataset.storeLocation || '';
      this.listContainer = this.querySelector('[data-careers-list]');
      this.careersUrl = '/pages/career-opportunities';
    }

    connectedCallback() {
      if (!this.listContainer) return;
      this.fetchCareers();
    }

    async fetchCareers() {
      try {
        const response = await fetch(this.careersUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch careers (' + response.status + ')');
        }

        const html = await response.text();
        this.parseCareers(html);
      } catch (error) {
        console.error('Error fetching careers:', error);
        this.showEmptyState();
      }
    }

    parseCareers(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const smoothieList = doc.querySelector('#smoothie-search-list');

      if (!smoothieList) {
        this.showEmptyState();
        return;
      }

      const allCareerItems = smoothieList.querySelectorAll('.smoothie-job-listing-card');
      const filteredCareers = Array.from(allCareerItems).filter((item) => {
        const tag = item.getAttribute('tag');
        return tag && tag.trim() === this.storeLocation;
      });

      if (filteredCareers.length === 0) {
        this.showEmptyState();
      } else {
        this.renderCareers(filteredCareers);
      }
    }

    renderCareers(careers) {
      const cardsHtml = careers
        .map((career) => {
          const title = career.querySelector('h3')?.textContent.trim() || '';
          const location = career.getAttribute('location') || '';
          const jobType = career.getAttribute('job-type') || '';
          const link = career.closest('a')?.getAttribute('href') || '#';
          return this.createCareerCard(title, location, jobType, link);
        })
        .join('');

      this.listContainer.innerHTML = cardsHtml;
    }

    createCareerCard(title, location, jobType, link) {
      const formattedJobType = this.formatJobType(jobType);

      return `
        <a href="${link}" class="flagship-store-career-card" target="_blank" rel="noopener">
          <h3 class="flagship-store-career-card__title">${title}</h3>
          <div class="flagship-store-career-card__details">
            ${
              location
                ? `
              <div class="flagship-store-career-card__detail">
                <span class="flagship-store-career-card__icon">${this.getLocationIcon()}</span>
                <span>${location}</span>
              </div>
            `
                : ''
            }
            ${
              formattedJobType
                ? `
              <div class="flagship-store-career-card__detail">
                <span class="flagship-store-career-card__icon">${this.getBriefcaseIcon()}</span>
                <span>${formattedJobType}</span>
              </div>
            `
                : ''
            }
          </div>
        </a>
      `;
    }

    formatJobType(jobType) {
      if (!jobType) return '';

      return jobType
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    showEmptyState() {
      const emptyTitle =
        this.dataset.emptyMessage || 'There are no open positions at the moment.';

      const emptyDescription =
        this.dataset.emptyDescription ||
        "<p>However, we're always open to connecting with talented individuals who share our vision. Feel free to reach out and we'll keep you in mind for future opportunities.</p>";

      this.listContainer.innerHTML = `
        <div class="flagship-store-careers__empty">
          <h3 class="flagship-store-careers__empty-title">${emptyTitle}</h3>
          <div class="flagship-store-careers__empty-description rte">
            ${emptyDescription}
          </div>
        </div>
      `;
    }

    getLocationIcon() {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8.00065 1.3335C5.42065 1.3335 3.33398 3.42016 3.33398 6.00016C3.33398 9.50016 8.00065 14.6668 8.00065 14.6668C8.00065 14.6668 12.6673 9.50016 12.6673 6.00016C12.6673 3.42016 10.5807 1.3335 8.00065 1.3335ZM8.00065 7.66683C7.55862 7.66683 7.1347 7.49123 6.82214 7.17867C6.50958 6.86611 6.33398 6.44219 6.33398 6.00016C6.33398 5.55814 6.50958 5.13421 6.82214 4.82165C7.1347 4.50909 7.55862 4.3335 8.00065 4.3335C8.44268 4.3335 8.8666 4.50909 9.17916 4.82165C9.49172 5.13421 9.66732 5.55814 9.66732 6.00016C9.66732 6.44219 9.49172 6.86611 9.17916 7.17867C8.8666 7.49123 8.44268 7.66683 8.00065 7.66683Z" fill="currentColor"/>
        </svg>
      `;
    }

    getBriefcaseIcon() {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10.5 9H5.5V8H0.5V14.125C0.5 14.2245 0.539509 14.3198 0.609835 14.3902C0.680161 14.4605 0.775544 14.5 0.875 14.5H15.125C15.2245 14.5 15.3198 14.4605 15.3902 14.3902C15.4605 14.3198 15.5 14.2245 15.5 14.125V8H10.5V9ZM15.5 3.875C15.5 3.77554 15.4605 3.68016 15.3902 3.60983C15.3198 3.53951 15.2245 3.5 15.125 3.5H12V1.75C12 1.6837 11.9737 1.62011 11.9268 1.57322C11.8799 1.52634 11.8163 1.5 11.75 1.5H4.25C4.1837 1.5 4.12011 1.52634 4.07322 1.57322C4.02634 1.62011 4 1.6837 4 1.75V3.5H0.875C0.775544 3.5 0.680161 3.53951 0.609835 3.60983C0.539509 3.68016 0.5 3.77554 0.5 3.875V7H15.5V3.875ZM10.75 3.5H5.25V2.75H10.75V3.5Z" fill="currentColor"/>
        </svg>
      `;
    }
  }

  customElements.define('flagship-store-careers', FlagshipStoreCareers);
}
